import { supabaseFetch } from './core';
import { devLog } from '../log';

// ============================================
// EXERCISE TEMPLATES
// ============================================

/**
 * Get all exercise templates with their muscle groups
 */
export const getExerciseTemplates = async () => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getExerciseTemplates called');

    // Fetch all templates
    const templates = await supabaseFetch('/exercise_templates?status=eq.active&select=*&order=name.asc');

    if (!templates || templates.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No exercise templates found');
      return [];
    }

    // Fetch all secondary muscles
    const templateIds = templates.map(t => t.id).join(',');
    const secondaryMuscles = await supabaseFetch(
      `/exercise_secondary_muscles?exercise_template_id=in.(${templateIds})&select=*`
    );

    // Combine results
    const result = templates.map(template => ({
      id: template.id,
      name: template.name,
      description: template.description,
      primaryMuscle: template.primary_muscle_group,
      exerciseType: template.exercise_type || 'reps', // reps/time/hold/cardio/distance
      usesWeight: template.uses_weight, // catalogo pulito: false = niente kg
      secondaryMuscles: secondaryMuscles
        .filter(sm => sm.exercise_template_id === template.id)
        .map(sm => ({
          muscleGroup: sm.muscle_group,
          percentage: sm.involvement_percentage
        }))
    }));

    /*#__PURE__*/ devLog('✅ [DATABASE] getExerciseTemplates successful, found', result.length, 'templates');
    return result;
  } catch (error) {
    console.error('❌ [DATABASE] getExerciseTemplates error:', error);
    return [];
  }
};

/**
 * Get detailed information for a specific exercise template
 * Used by ExerciseDetailModal to show descriptions, tips, mistakes, etc.
 */
export const getExerciseTemplateDetails = async (templateId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getExerciseTemplateDetails called for:', templateId);

  try {
    const data = await supabaseFetch(
      `/exercise_templates?id=eq.${templateId}&select=*,exercise_secondary_muscles(muscle_group,involvement_percentage,source,confidence,source_ref)`
    );

    if (data && data.length > 0) {
      /*#__PURE__*/ devLog('✅ [DATABASE] Exercise template details loaded');
      return data[0];
    }

    throw new Error('Exercise template not found');
  } catch (error) {
    console.error('❌ [DATABASE] getExerciseTemplateDetails error:', error);
    throw error;
  }
};

/**
 * Get all exercise templates with full raw data (admin use only).
 * Unlike getExerciseTemplates, returns all columns for quality auditing and editing.
 */
export const getExerciseTemplatesAdmin = async () => {
  try {
    const templates = await supabaseFetch('/exercise_templates?status=eq.active&select=*&order=name.asc');
    return templates || [];
  } catch (error) {
    console.error('❌ [DATABASE] getExerciseTemplatesAdmin error:', error);
    return [];
  }
};

/**
 * Get exercise template details by name (fuzzy match)
 * Used as fallback when exercise_template_id is not available
 */
export const getExerciseTemplateByName = async (exerciseName) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getExerciseTemplateByName called for:', exerciseName);

  try {
    // Try exact match first
    let templates = await supabaseFetch(
      `/exercise_templates?status=eq.active&name=ilike.${encodeURIComponent(exerciseName)}&select=*,exercise_secondary_muscles(muscle_group,involvement_percentage,source,confidence,source_ref)`
    );

    if (templates && templates.length > 0) {
      /*#__PURE__*/ devLog('✅ [DATABASE] Exercise template found by exact name');
      return templates[0];
    }

    // Try partial match (exercise name contains template name or vice versa)
    templates = await supabaseFetch(
      `/exercise_templates?status=eq.active&name=ilike.*${encodeURIComponent(exerciseName.split(' ')[0])}*&select=*,exercise_secondary_muscles(muscle_group,involvement_percentage,source,confidence,source_ref)&limit=5`
    );

    if (templates && templates.length > 0) {
      // Find best match by comparing names
      const lowerName = exerciseName.toLowerCase();
      const bestMatch = templates.find(t =>
        lowerName.includes(t.name.toLowerCase()) ||
        t.name.toLowerCase().includes(lowerName)
      );

      if (bestMatch) {
        /*#__PURE__*/ devLog('✅ [DATABASE] Exercise template found by partial match:', bestMatch.name);
        return bestMatch;
      }
    }

    /*#__PURE__*/ devLog('⚠️ [DATABASE] No matching template found for:', exerciseName);
    return null;
  } catch (error) {
    console.error('❌ [DATABASE] getExerciseTemplateByName error:', error);
    return null;
  }
};

/**
 * Create a new exercise template
 */
export const createExerciseTemplate = async (templateData) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] createExerciseTemplate called');

  try {
    const data = await supabaseFetch('/exercise_templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] Exercise template created');
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] createExerciseTemplate error:', error);
    throw error;
  }
};

/**
 * Update an existing exercise template
 */
export const updateExerciseTemplate = async (templateId, templateData) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] updateExerciseTemplate called for:', templateId);

  try {
    const response = await supabaseFetch(`/exercise_templates?id=eq.${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(templateData)
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] Exercise template updated');
    return true;
  } catch (error) {
    console.error('❌ [DATABASE] updateExerciseTemplate error:', error);
    throw error;
  }
};

/**
 * Delete an exercise template
 */
export const deleteExerciseTemplate = async (templateId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] deleteExerciseTemplate called for:', templateId);

  try {
    const response = await supabaseFetch(`/exercise_templates?id=eq.${templateId}`, {
      method: 'DELETE'
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] Exercise template deleted');
    return true;
  } catch (error) {
    console.error('❌ [DATABASE] deleteExerciseTemplate error:', error);
    throw error;
  }
};

/**
 * Search exercise templates by name
 */
/**
 * Ritorna gli id dei template che matchano per NOME o ALIAS (normalizzato,
 * parziale) via RPC. Alla base della ricerca catalogo e della dedup richieste.
 */
export const searchExerciseIds = async (query) => {
  try {
    const rows = await supabaseFetch('/rpc/search_exercise_ids', {
      method: 'POST',
      body: JSON.stringify({ p_q: query || '' }),
    });
    return Array.isArray(rows) ? rows.map((r) => r.id) : [];
  } catch (err) {
    console.warn('[exercises] searchExerciseIds error:', err.message);
    return [];
  }
};

export const searchExerciseTemplates = async (query) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] searchExerciseTemplates called with query:', query);

    if (!query || query.trim().length < 2) {
      return await getExerciseTemplates();
    }

    // Match per nome O alias (normalizzato) via RPC
    const ids = await searchExerciseIds(query);

    // Fallback ILIKE sul nome se la RPC non è disponibile (migration non ancora
    // eseguita) o non trova nulla → la ricerca non regredisce mai.
    const templates = ids.length > 0
      ? await supabaseFetch(
          `/exercise_templates?id=in.(${ids.join(',')})&status=eq.active&select=*&order=name.asc`
        )
      : await supabaseFetch(
          `/exercise_templates?status=eq.active&name=ilike.*${encodeURIComponent(query)}*&select=*&order=name.asc&limit=20`
        );

    if (!templates || templates.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No templates found matching query');
      return [];
    }

    // Fetch secondary muscles for results
    const templateIds = templates.map(t => t.id).join(',');
    const secondaryMuscles = await supabaseFetch(
      `/exercise_secondary_muscles?exercise_template_id=in.(${templateIds})&select=*`
    );

    const result = templates.map(template => ({
      id: template.id,
      name: template.name,
      description: template.description,
      primaryMuscle: template.primary_muscle_group,
      exerciseType: template.exercise_type || 'reps', // reps/time/hold/cardio/distance
      usesWeight: template.uses_weight, // catalogo pulito: false = niente kg
      secondaryMuscles: secondaryMuscles
        .filter(sm => sm.exercise_template_id === template.id)
        .map(sm => ({
          muscleGroup: sm.muscle_group,
          percentage: sm.involvement_percentage
        }))
    }));

    /*#__PURE__*/ devLog('✅ [DATABASE] searchExerciseTemplates successful, found', result.length, 'templates');
    return result;
  } catch (error) {
    console.error('❌ [DATABASE] searchExerciseTemplates error:', error);
    return [];
  }
};

/**
 * Find exercises similar to a given name (for duplicate detection).
 * Returns up to 3 active templates that share significant words with the query.
 */
export const findSimilarExercises = async (name) => {
  if (!name || name.trim().length < 3) return [];
  try {
    const trimmed = name.trim();

    // Match alias-aware su nome completo…
    let ids = await searchExerciseIds(trimmed);

    // …e sulla prima parola significativa, se il nome completo non trova nulla
    if (ids.length === 0) {
      const firstWord = trimmed.split(/\s+/).find((w) => w.length >= 3);
      if (firstWord) ids = await searchExerciseIds(firstWord);
    }

    // Fallback ILIKE se la RPC non è disponibile/non trova (pre-migration)
    const templates = ids.length > 0
      ? await supabaseFetch(
          `/exercise_templates?id=in.(${ids.slice(0, 3).join(',')})&status=eq.active&select=id,name,primary_muscle_group`
        )
      : await supabaseFetch(
          `/exercise_templates?status=eq.active&name=ilike.*${encodeURIComponent(trimmed)}*&select=id,name,primary_muscle_group&limit=3`
        );
    return Array.isArray(templates) ? templates.slice(0, 3) : [];
  } catch {
    return [];
  }
};
